/**
 * Pure helpers for the Ask modal (kanban d3ec3001): who a desk means, what a
 * chip stands for, who a selection fans out to, and the picker's filtering.
 * Kept out of the .tsx so it is testable without a DOM.
 */
import { CUSTOMER_DESK_WORDS } from './deskSplit';
import type {
  DoorAskPersonRead,
  DoorAskTargetsRead,
  DoorDataRequestRead,
} from '../../services/qcpDataRequests';

/** One chip in the To row. */
export type AskRecipientToken =
  | { kind: 'person'; name: string; email: string | null; unresolved?: boolean }
  | { kind: 'role'; token: string; label: string; person: DoorAskPersonRead | null }
  | { kind: 'team'; key: string; label: string; members: DoorAskPersonRead[] }
  | { kind: 'customer'; via: DoorAskPersonRead | null };

/** Words that mean the customer's end of the line. */
const CUSTOMER_WORDS = CUSTOMER_DESK_WORDS;

const ROLE_PHRASES: Record<string, 'sales_rep' | 'cs_rep'> = {
  'sales rep': 'sales_rep',
  sales: 'sales_rep',
  salesperson: 'sales_rep',
  'account rep': 'sales_rep',
  rep: 'sales_rep',
  regional: 'sales_rep',
  'cs rep': 'cs_rep',
  cs: 'cs_rep',
  csr: 'cs_rep',
  'customer service rep': 'cs_rep',
  'customer service': 'cs_rep',
};

const fold = (s: string) => s.trim().toLowerCase().replace(/\s+/g, ' ');
const nameWords = (s: string) =>
  s
    .toLowerCase()
    .split(/[^a-z]+/)
    .filter((w) => w.length >= 3);

/** `"Cliff / customer"` → `["Cliff", "customer"]`. */
export function deskParts(desk: string): string[] {
  return desk
    .split(/\s*(?:\/|&|\band\b|,|\+)\s*/i)
    .map((p) => p.trim())
    .filter(Boolean);
}

/** "Laura Caswell's team" and "Customer service desk" stay as they are;
 *  "Delta" becomes "Delta team". */
export function teamLabel(label: string): string {
  return /\b(team|desk)$/i.test(label.trim()) ? label.trim() : `${label.trim()} team`;
}

/** The key of a token, for equality and dedupe. */
export function tokenKey(t: AskRecipientToken): string {
  switch (t.kind) {
    case 'person':
      return `person:${fold(t.email || t.name)}`;
    case 'role':
      return `role:${t.token}`;
    case 'team':
      return `team:${t.key}`;
    case 'customer':
      return 'customer';
  }
}

/** What the send call takes for a token. */
export function tokenString(t: AskRecipientToken): string {
  switch (t.kind) {
    case 'person':
      return t.email || t.name;
    case 'role':
      return t.token;
    case 'team':
      return `team:${t.key}`;
    case 'customer':
      return 'customer';
  }
}

/** The chip a person option adds. */
export function personToken(p: DoorAskPersonRead): AskRecipientToken {
  return { kind: 'person', name: p.name, email: p.email };
}

/**
 * A draft names a person at the address it was stamped with; the directory
 * is what says where they get work mail now (a personal address on a
 * duplicate staff row is dropped there, kanban fb27b753). The draft's address
 * stands when the directory has not loaded or still lists it.
 */
export function directoryPerson(
  name: string,
  email: string | null,
  targets: DoorAskTargetsRead | null,
): AskRecipientToken {
  if (targets) {
    const listed = email && targets.staff.find((p) => p.email && fold(p.email) === fold(email));
    if (!listed) {
      const sameName = targets.staff.filter((p) => fold(p.name) === fold(name) && p.email);
      if (sameName.length === 1) return personToken(sameName[0]);
    }
  }
  return { kind: 'person', name, email };
}

/** One desk part → a token, resolved against the targets when they are loaded. */
export function tokenForDeskPart(
  part: string,
  targets: DoorAskTargetsRead | null,
  viewerName: string | null,
): AskRecipientToken | null {
  const key = fold(part);
  if (!key) return null;
  const words = nameWords(part);
  const viewer = new Set(nameWords(viewerName ?? ''));
  if (['us', 'we', 'me', 'ours'].includes(key)) return null;
  if (words.length > 0 && viewer.size > 0 && words.every((w) => viewer.has(w))) return null;
  if (CUSTOMER_WORDS.has(key)) return { kind: 'customer', via: targets?.customer_relay ?? null };
  if (targets) {
    // A function desk ("accounting", "a/r", "the lab") answers to every
    // spelling the backend lists for it (kanban 196e9d9b).
    const team = targets.teams.find(
      (t) =>
        fold(t.label) === key ||
        fold(t.key) === key ||
        key === `team:${fold(t.key)}` ||
        fold(t.label.replace(/'s team$/, "'s team")) === key ||
        (t.aliases ?? []).some((a) => fold(a) === key),
    );
    if (team) return teamToken(team);
  }
  const roleKey = key.startsWith('role:') ? key.slice(5) : ROLE_PHRASES[key];
  if (roleKey === 'sales_rep' || roleKey === 'cs_rep') {
    const role = targets?.roles.find((r) => r.role === roleKey);
    // Nobody fills the role on this account: the desk that covers it takes
    // the ask (the CS desk for "the CS rep"), as the backend resolves it.
    if (targets && !role?.resolved_name) {
      const desk = targets.teams.find((t) => t.covers_role === roleKey && t.members.length > 0);
      if (desk) return teamToken(desk);
    }
    return {
      kind: 'role',
      token: `role:${roleKey}`,
      label: role?.label ?? (roleKey === 'sales_rep' ? "Account's sales rep" : "Account's CS rep"),
      person:
        role?.resolved_name ? { name: role.resolved_name, email: role.resolved_email } : null,
    };
  }
  if (targets) {
    const exact = targets.staff.find(
      (p) => fold(p.name) === key || (p.email && fold(p.email) === key),
    );
    if (exact) return personToken(exact);
    const loose = targets.staff.filter((p) =>
      fold(p.name)
        .split(' ')
        .some((w) => w.startsWith(key)) || fold(p.name).includes(key),
    );
    if (loose.length === 1) return personToken(loose[0]);
    return { kind: 'person', name: part.trim(), email: null, unresolved: true };
  }
  return { kind: 'person', name: part.trim(), email: null };
}

/**
 * The To row the modal opens with. Drafts Merlin proposed for the step name
 * the recipients exactly (a `customer_relay` row is the rep, shown as the
 * customer chip); without drafts the desk text is resolved part by part.
 */
export function defaultRecipients(
  desk: string,
  drafts: DoorDataRequestRead[],
  targets: DoorAskTargetsRead | null,
  viewerName: string | null,
): AskRecipientToken[] {
  const out: AskRecipientToken[] = [];
  const push = (t: AskRecipientToken | null) => {
    if (t && !out.some((o) => tokenKey(o) === tokenKey(t))) out.push(t);
  };
  if (drafts.length > 0) {
    drafts.forEach((d) => {
      const src = d.recipient_source ?? 'named';
      if (src === 'customer_relay') {
        push({ kind: 'customer', via: { name: d.target_name, email: d.target_email } });
      } else if (src.startsWith('role:') || src.startsWith('team:')) {
        // The role or team chip once the directory is here; until then (or
        // when the directory no longer lists it) the draft's own person —
        // never a "team:fn_accounting" pseudo-name on the chip (local E2E,
        // kanban 196e9d9b).
        const resolved = targets ? tokenForDeskPart(src, targets, viewerName) : null;
        push(
          resolved && resolved.kind !== 'person'
            ? resolved
            : directoryPerson(d.target_name, d.target_email, targets),
        );
      } else {
        push(directoryPerson(d.target_name, d.target_email, targets));
      }
    });
    return out;
  }
  deskParts(desk).forEach((part) => push(tokenForDeskPart(part, targets, viewerName)));
  return out;
}

/** The people a selection reaches, deduped, each with where they came from. */
export function expandRecipients(
  tokens: AskRecipientToken[],
): Array<{ person: DoorAskPersonRead; from: string | null }> {
  const out: Array<{ person: DoorAskPersonRead; from: string | null }> = [];
  const seen = new Set<string>();
  const add = (p: DoorAskPersonRead, from: string | null) => {
    const k = fold(p.email || p.name);
    if (seen.has(k)) return;
    seen.add(k);
    out.push({ person: p, from });
  };
  tokens.forEach((t) => {
    if (t.kind === 'person' && !t.unresolved) add({ name: t.name, email: t.email }, null);
    if (t.kind === 'role' && t.person) add(t.person, t.label);
    if (t.kind === 'team') t.members.forEach((m) => add(m, teamLabel(t.label)));
    if (t.kind === 'customer' && t.via) add(t.via, 'relays to the customer');
  });
  return out;
}

/** Why Send is disabled, or null when it can go. */
export function sendBlocker(tokens: AskRecipientToken[]): string | null {
  const unresolved = tokens.filter((t) => t.kind === 'person' && t.unresolved) as Array<
    Extract<AskRecipientToken, { kind: 'person' }>
  >;
  if (unresolved.length > 0) {
    return `Nobody in the staff directory is called "${unresolved[0].name}". Pick who you mean below, or remove them.`;
  }
  const roleless = tokens.find((t) => t.kind === 'role' && !t.person);
  if (roleless && roleless.kind === 'role') {
    return `Nobody fills the ${roleless.label.toLowerCase()} for this account. Pick a person instead.`;
  }
  const customer = tokens.find((t) => t.kind === 'customer');
  if (customer && customer.kind === 'customer' && !customer.via && !customerCarrier(tokens)) {
    return 'The customer is reached through the account\'s sales rep, and this account has none on file. Add the person who talks to them.';
  }
  if (expandRecipients(tokens).length === 0) return 'Nobody to send to yet.';
  return null;
}

/** Who carries the customer's part: the account's rep, else the first named
 *  person on the desk (a house account has no rep; Cliff is right there). */
export function customerCarrier(tokens: AskRecipientToken[]): DoorAskPersonRead | null {
  const customer = tokens.find((t) => t.kind === 'customer');
  if (!customer || customer.kind !== 'customer') return null;
  if (customer.via) return customer.via;
  const named = tokens.find((t) => t.kind === 'person' && !t.unresolved);
  return named && named.kind === 'person' ? { name: named.name, email: named.email } : null;
}

/** The "How they get it" line: how the ask reaches the people on it. Two or
 *  more people share ONE email thread (everyone on To); each still gets their
 *  own widget card and Slack / text. */
export function deliveryLine(tokens: AskRecipientToken[]): string {
  const people = expandRecipients(tokens);
  if (people.length > 1) {
    return 'one email from merlin@ to all of them, with you copied, so everyone sees every reply; '
      + 'each also gets a card in the sales-site widget, and Slack or a text where those are on. '
      + 'Replies on any of them land here.';
  }
  return 'email from merlin@ with you copied, a card in the sales-site widget, and Slack or a '
    + 'text where those are on. Replies on any of them land here.';
}

/** The one line under the chips: who gets it, in words. */
export function fanoutLine(tokens: AskRecipientToken[]): string {
  const people = expandRecipients(tokens);
  const customer = tokens.find((t) => t.kind === 'customer');
  let line = '';
  if (people.length === 1) {
    line = `Goes to ${people[0].person.name}${people[0].from && people[0].from !== 'relays to the customer' ? ` (${people[0].from})` : ''}.`;
  } else if (people.length > 1) {
    line = `Goes to ${people.length} people: ${people
      .map((p) => p.person.name + (p.from && p.from !== 'relays to the customer' ? ` (${p.from})` : ''))
      .join(', ')}. One email to all of them (each still gets their own card and Slack note); the first to answer closes it and everyone on the thread is told.`;
  }
  const carrier = customerCarrier(tokens);
  if (customer && carrier) {
    line += ` Merlin does not write to customers: ${carrier.name.split(' ')[0]} gets the message and relays it.`;
  }
  return line.trim();
}

export type FilteredTargets = {
  roles: DoorAskTargetsRead['roles'];
  teams: DoorAskTargetsRead['teams'];
  staff: DoorAskPersonRead[];
};

/**
 * The picker's options for what is typed, by the route picker's rule
 * (routePickView.ts, E2E pass 2): a role by its label or the person it
 * resolves to, a team by its name, a person by their name; case-insensitive
 * "contains", everyone when the box is empty. A team no longer shows because
 * one of its members matched, and emails are not searched: typing "Jess"
 * lists the Jesses, not every team with a Jess in it.
 */
export function filterTargets(targets: DoorAskTargetsRead, query: string): FilteredTargets {
  const q = fold(query);
  const hit = (...parts: Array<string | null | undefined>) =>
    !q || parts.some((p) => p && fold(p).includes(q));
  return {
    roles: targets.roles.filter((r) => hit(r.label, r.resolved_name)),
    teams: targets.teams.filter((t) => hit(t.label)),
    staff: targets.staff.filter((p) => hit(p.name)),
  };
}

/** How many options the filter left. */
export function targetCount(f: FilteredTargets): number {
  return f.roles.length + f.teams.length + f.staff.length;
}

/** The chip a role option adds. */
export function roleToken(r: DoorAskTargetsRead['roles'][number]): AskRecipientToken {
  return {
    kind: 'role',
    token: r.token,
    label: r.label,
    person: r.resolved_name ? { name: r.resolved_name, email: r.resolved_email } : null,
  };
}

/** The chip a team option adds. */
export function teamToken(t: DoorAskTargetsRead['teams'][number]): AskRecipientToken {
  return { kind: 'team', key: t.key, label: t.label, members: t.members };
}

/**
 * Exactly one option matches what is typed, and it can be asked: that one is
 * picked (the route picker's "exactly one match selects"). Anything else
 * (nothing typed, none, several, or the one match has nobody to send to)
 * picks nothing until an option is clicked.
 */
export function soleMatch(f: FilteredTargets, query: string): AskRecipientToken | null {
  if (!fold(query) || targetCount(f) !== 1) return null;
  const [role] = f.roles;
  if (role) return role.available ? roleToken(role) : null;
  const [team] = f.teams;
  if (team) return team.members.length > 0 ? teamToken(team) : null;
  const [person] = f.staff;
  return person.email ? personToken(person) : null;
}

/** Toggle a token in the To row. */
export function toggleToken(tokens: AskRecipientToken[], t: AskRecipientToken): AskRecipientToken[] {
  const key = tokenKey(t);
  return tokens.some((o) => tokenKey(o) === key)
    ? tokens.filter((o) => tokenKey(o) !== key)
    : [...tokens, t];
}

/** The text of the open steps, as the message box's fallback for an older
 *  filing that carries no pre-written ask. */
/**
 * "What you are asking" when Merlin wrote no draft: the first open step,
 * without its desk ("Sales (Ashton): report back …" → "Report back …"),
 * cut at a word near 90 characters. UX round 2026-10-27: the box started
 * empty while the message under it was already written from the same
 * step, and Ask stayed greyed with nothing saying why.
 */
export function fallbackTitle(steps: string[]): string {
  const step = steps.map((s) => s.replace(/\s+/g, ' ').trim()).find(Boolean) ?? '';
  const bare = step.replace(/^[^:]{1,40}:\s+/, '');
  const text = bare.charAt(0).toUpperCase() + bare.slice(1);
  if (text.length <= 90) return text;
  const cut = text.slice(0, 90);
  const space = cut.lastIndexOf(' ');
  return `${(space > 40 ? cut.slice(0, space) : cut).replace(/[\s,;:—-]+$/, '')}…`;
}

export function fallbackBody(threadNo: string, steps: string[]): string {
  const lines = steps.map((s) => `- ${s}`);
  return [`On ${threadNo}, could you help with ${lines.length === 1 ? 'this' : 'these'}:`, ...lines, '', 'A reply here reaches Merlin and me.'].join('\n');
}
